import React, {useEffect, useState} from 'react'
import TopInformation from '../../components/shared/topinformation/TopInformation.js'
import {useTranslation} from 'react-i18next'
import {useDispatch, useSelector} from 'react-redux'
import {Button} from "react-bootstrap"
import InformationModal from "../../components/shared/informationmodal/InformationModal.js"
import CustomerUpdateTableBody from "../../components/firmware/customerupdatetable/CustomerUpdateTableBody.js"
import {constructPath, createPathStringFromArray, findGroup} from "../../helpers/helperfunctions.js"
import {cloneDeep, difference, orderBy, uniq} from "lodash"
import useSetLocation from "../../helpers/hooks/useSetLocation.js"
import {
    applyCustomerFirmwareUpdates,
    getCustomerFirmwareUpdates
} from "../../helpers/reduxstore/reducers/customerFirmwareUpdatesReducer.js"

/** Renders the page where a customer can see and apply firmware updates released to them by an Admin */
export default function UpdatesPage() {
    useSetLocation()

    const {t} = useTranslation(['updatespage', 'common'])
    const dispatch = useDispatch()
    const {list} = useSelector(state => state.customerFirmwareUpdates)
    const {allowedGroups} = useSelector(state => state.group)
    const [deviceListLoading, setDeviceListLoading] = useState(true)
    const [deviceListWithCheck, setDeviceListWithCheck] = useState([])
    const [checkedDeviceIdList, setCheckedDeviceIdList] = useState([])
    const [showConfirmation, setShowConfirmation] = useState(false)
    const [sort, setSort] = useState({sortDirection: "asc", sortValue: "serial_number"})

    useEffect(() => {
        dispatch(getCustomerFirmwareUpdates()).then(() => setDeviceListLoading(false))
    }, [dispatch])

    useEffect(() => {
        const newDeviceListWithCheck = cloneDeep(list).map(device => deviceWithPathAndCheck(device))
        for (const deviceId of checkedDeviceIdList) {
            const foundDevice = newDeviceListWithCheck.find(d => d.id === deviceId)
            if (foundDevice) foundDevice.checked = true
        }
        setDeviceListWithCheck(newDeviceListWithCheck)
    }, [list])

    function deviceWithPathAndCheck(device) {
        device.path = createPathStringFromArray(constructPath(findGroup(device.parent, allowedGroups), allowedGroups), device.name)
        device.checked = false
        return device
    }

    function toggleRowChecked(row, value) {
        const newDeviceList = cloneDeep(deviceListWithCheck)
        newDeviceList.find(d => d.id === row.id).checked = value
        setCheckedDeviceIdList(value ? [...checkedDeviceIdList, row.id] : checkedDeviceIdList.filter(id => id !== row.id))
        setDeviceListWithCheck(newDeviceList)
    }

    const sortHandler = (headerData) => {
        setSort({
            sortDirection: headerData.sortValue === sort.sortValue && sort.sortDirection === 'asc' ? 'desc' : "asc",
            sortValue: headerData.sortValue
        })
    }

    function toggleAllShownRows() {
        const newDeviceList = cloneDeep(deviceListWithCheck)
        const allChecked = deviceListWithCheck.every(d => d.checked)
        if (!allChecked) setCheckedDeviceIdList(uniq([...checkedDeviceIdList, ...newDeviceList.map(d => d.id)]))
        else setCheckedDeviceIdList(difference(checkedDeviceIdList, newDeviceList.map(d => d.id)))
        for (const device of newDeviceList) {
            device.checked = !allChecked
        }
        setDeviceListWithCheck(newDeviceList)
    }

    const commitChanges = async () => {
        setShowConfirmation(false)
        await dispatch(applyCustomerFirmwareUpdates(checkedDeviceIdList))
        setCheckedDeviceIdList([])
    }

    return (
        <React.Fragment>
            <InformationModal show={showConfirmation} title={t('updatespage:modal.confirm.title')}
                              body={t('updatespage:modal.confirm.text', {count: checkedDeviceIdList.length})}
                              dismiss={() => setShowConfirmation(false)} accept={commitChanges}/>
            <div className="d-flex flex-column" style={{flex: '1'}}>

                <TopInformation title={t('updatespage:info_section.info_section_title')}
                                subTitle={t('updatespage:info_section.info_section_p')}/>

                <div className='mb-2 mt-4'>
                <span
                    className="text-nowrap small">{`${t('updatespage:table.headers.total')} ${deviceListWithCheck.length}`}</span>
                </div>

                <CustomerUpdateTableBody className='mb-2'
                                         deviceList={sort.sortValue ? orderBy(deviceListWithCheck, [sort.sortValue], [sort.sortDirection]) : deviceListWithCheck}
                                         deviceListLoading={deviceListLoading}
                                         sort={sort}
                                         sortHandler={sortHandler}
                                         toggleRowChecked={toggleRowChecked}
                                         toggleAllShownRows={toggleAllShownRows}/>

                <div className="d-flex pb-3 align-items-center">
                    <Button variant="primary" onClick={() => setShowConfirmation(true)}
                            disabled={checkedDeviceIdList.length === 0}
                            type="submit">{t('updatespage:buttons.update_devices', {count: checkedDeviceIdList.length})}
                    </Button>
                </div>
            </div>
        </React.Fragment>
    )
}
